<template>
  <div
    v-for="visibleEntry in visibleLogEntries"
    :key="visibleEntry.index"
    class="expedition-log-item"
    :data-shown="logStates[visibleEntry.index] || !log.shouldPlayAnimation ? 'true' : 'false'"
  >
    <div class="expedition-log-item-text">
      {{
        Formatters.date.toReadableTime(new Date(entryTimestampMs(visibleEntry.index)), { seconds: true }) +
        ' ' +
        visibleEntry.value
      }}
    </div>
    <div v-if="log.image && visibleEntry.index === visibleLogEntries.length - 1" class="expedition-log-image">
      <img :src="log.image" alt="Expedition Log Image" />
    </div>
    <div
      v-if="log.items?.length && visibleEntry.index === visibleLogEntries.length - 1"
      class="expedition-log-item-content"
    >
      <div v-for="logItem in log.items || []" :key="logItem.id" class="expedition-log-item-content-item">
        <div class="expedition-log-item-content-item-icon">
          <img :src="halloweenItemRegistry.resolveIcon(logItem)" alt="Item Icon" />
        </div>
        <span :class="['expedition-log-item-content-item-amount', { red: logItem.quantity < 0 }]">
          {{ Formatters.number.formatDelta(logItem.quantity) }}
        </span>
      </div>
    </div>
    <div v-if="visibleEntry.index === 0 && logIndex !== logsCount - 1" class="expedition-log-delimiter" />
  </div>
</template>
<script setup lang="ts">
import { ethers } from 'ethers';
import type { ExpeditionLog } from '~/types/halloween/expedition';
import { Formatters } from '~/utils/formatters/formatters';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';

const { log, expeditionId } = defineProps<{
  log: ExpeditionLog;
  expeditionId: number;
  logIndex: number;
  logsCount: number;
}>();
const visibleLogEntries = ref<{ value: string; index: number }[]>([]);
const logStates = ref<Record<number, boolean>>({});
const animationInterval = ref<NodeJS.Timeout | null>(null);
const halloween = useHalloweenStore();
const timeout = ref<NodeJS.Timeout | null>(null);

const entryOffsetsMs = computed(() => {
  const length = log.text.length || 0;
  if (length <= 1) return [0];

  const seedHex = ethers.keccak256(ethers.toUtf8Bytes(log.uniqueEventIndex));
  const bytes = ethers.getBytes(seedHex);

  const incrementsSec: number[] = [];
  for (let i = 0; i < length; i++) {
    const b = bytes[i % bytes.length];
    const rnd01 = b / 255; // [0,1]
    const deltaSec = 2 + Math.floor(rnd01 * 9);
    incrementsSec.push(deltaSec);
  }

  const offsetsMs: number[] = [];
  let accMs = 0;
  for (let i = 0; i < length; i++) {
    offsetsMs.push(accMs);
    accMs += incrementsSec[i] * 1000;
  }
  return offsetsMs;
});

const entryTimestampMs = (entryIndex: number) => {
  const offsets = entryOffsetsMs.value;
  const idx = Math.min(Math.max(entryIndex, 0), offsets.length - 1);
  return log.timestampMs + (offsets[idx] || 0);
};

onMounted(() => {
  if (!log.shouldPlayAnimation) {
    visibleLogEntries.value = log.text.map((v, i) => ({ value: v, index: i })).reverse();
    return;
  }

  let _id = 0;
  const iterateLogs = () => {
    if (visibleLogEntries.value.length >= log.text.length) {
      halloween.markTextAnimationAsPlayed(expeditionId, log);
      if (animationInterval.value) clearInterval(animationInterval.value);
      return;
    }

    const val = log.text[visibleLogEntries.value.length];
    if (visibleLogEntries.value.length > 0) logStates.value[visibleLogEntries.value.length - 1] = true;
    if (val) visibleLogEntries.value.unshift({ value: val, index: _id++ });
  };

  animationInterval.value = setInterval(() => {
    iterateLogs();
  }, 3000);

  iterateLogs();
});

onUnmounted(() => {
  if (timeout.value) {
    clearTimeout(timeout.value);
    timeout.value = null;
  }
  if (animationInterval.value) {
    clearInterval(animationInterval.value);
  }
});
</script>

<style scoped>
.expedition-log-item {
  transition: 300ms ease all;

  * {
    font-family:
      PT Serif,
      sans-serif;
  }
}

.expedition-log-item {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  pointer-events: none;
  transition: 300ms ease all;

  &[data-shown='false'] {
    opacity: 0;
    position: absolute;
    transform: translateY(-10px);
  }

  &[data-shown='true'] {
    transform: translateY(0);
    opacity: 1;
    position: relative;
  }
}

.expedition-log-delimiter {
  width: 100%;
  height: 1px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
  margin: 13px 0 12px;
  position: relative;
}

.expedition-log-item-text {
  font-family:
    PT Serif,
    serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  color: #cdcdcd;
  width: 100%;
}

.expedition-log-item-content {
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  gap: 10px;
}

.expedition-log-item-content-item {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
}

.expedition-log-item-content-item-icon {
  overflow: hidden;
  width: 28px !important;
  height: 28px;
  min-width: 28px;
  max-width: 28px !important;

  img {
    width: 100%;
    height: auto;
  }
}
.expedition-log-item-content-item-amount {
  font-family:
    PT Serif,
    serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 18px;
  color: #3fc096;

  &.red {
    color: #fb4458;
  }
}

.expedition-log-image {
  position: relative;
  display: block;
  width: 60%;
  height: fit-content;
  overflow: hidden;
  border-radius: 6px;

  img {
    width: 100%;
    height: auto;
  }
}
</style>
